// =====================================================
// GESTIÓN DE CONTRASEÑAS CON LOCALSTORAGE
// =====================================================

/**
 * Sistema de gestión de contraseñas que usa localStorage para persistencia inmediata
 * Los cambios son efectivos al instante en la webapp
 */

// Obtener datos originales
import database from '../database.json';

const STORAGE_KEY = 'pe_password_overrides';
const ADMIN_STORAGE_KEY = 'pe_admin_password_override';

/**
 * Obtener contraseñas actuales (con overrides aplicados)
 */
export const getCurrentPasswords = () => {
  const overrides = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  const adminOverride = localStorage.getItem(ADMIN_STORAGE_KEY);

  const result = {
    clients: {},
    admin: adminOverride || process.env.REACT_APP_ADMIN_PASSWORD_HASH
  };

  // Para cada cliente, usar override si existe, sino el original
  database.clientes.forEach(client => {
    result.clients[client.id] = overrides[client.id] || client.passwordHash;
  });

  return result;
};

/**
 * Obtener contraseña actual de un cliente específico (hash)
 */
export const getClientCurrentPassword = (clientId) => {
  const passwords = getCurrentPasswords();
  return passwords.clients[clientId];
};

/**
 * Obtener estado de contraseña para mostrar (nunca expone contraseñas reales)
 */
export const getDisplayPassword = (clientId) => {
  const overrides = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  const adminOverride = localStorage.getItem(ADMIN_STORAGE_KEY);

  // Si hay override en localStorage, mostrar que fue cambiada
  if (clientId === 'ADMIN' && adminOverride) {
    return '[Modificada - pendiente de aplicar]';
  }

  if (overrides[clientId]) {
    return '[Modificada - pendiente de aplicar]';
  }

  // Verificar si tiene hash configurado en database
  if (clientId === 'ADMIN') {
    return process.env.REACT_APP_ADMIN_PASSWORD_HASH ? '[Configurada]' : '[No configurada]';
  }

  const client = database.clientes.find(c => c.id === clientId);
  return client?.passwordHash ? '[Configurada]' : '[No configurada]';
};

/**
 * Verificar si una contraseña coincide (considerando overrides)
 */
export const verifyPasswordWithOverrides = async (clientId, password, verifyPasswordFn) => {
  const passwords = getCurrentPasswords();
  
  if (clientId === 'ADMIN') {
    return await verifyPasswordFn(password, passwords.admin);
  }
  
  const clientHash = passwords.clients[clientId];
  return await verifyPasswordFn(password, clientHash);
};

/**
 * Cambiar contraseña de cliente (guarda en localStorage)
 */
export const changeClientPassword = (clientId, newPasswordHash) => {
  const overrides = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  overrides[clientId] = newPasswordHash;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  
  // Registrar el cambio para generar script después
  registerPasswordChange(clientId, newPasswordHash);
};

/**
 * Cambiar contraseña de admin
 */
export const changeAdminPassword = (newPasswordHash) => {
  localStorage.setItem(ADMIN_STORAGE_KEY, newPasswordHash);
  registerAdminPasswordChange(newPasswordHash);
};

/**
 * Registrar cambios para generar script de aplicación
 */
const CHANGES_KEY = 'pe_pending_changes';

const registerPasswordChange = (clientId, newPasswordHash) => {
  const changes = JSON.parse(localStorage.getItem(CHANGES_KEY) || '[]');
  
  // Remover cambio anterior para el mismo cliente
  const filteredChanges = changes.filter(c => c.type !== 'client' || c.clientId !== clientId);
  
  // Agregar nuevo cambio
  filteredChanges.push({
    type: 'client',
    clientId,
    newPasswordHash,
    timestamp: Date.now()
  });
  
  localStorage.setItem(CHANGES_KEY, JSON.stringify(filteredChanges));
};

const registerAdminPasswordChange = (newPasswordHash) => {
  const changes = JSON.parse(localStorage.getItem(CHANGES_KEY) || '[]');
  
  // Remover cambio admin anterior
  const filteredChanges = changes.filter(c => c.type !== 'admin');
  
  // Agregar nuevo cambio admin
  filteredChanges.push({
    type: 'admin',
    newPasswordHash,
    timestamp: Date.now()
  });
  
  localStorage.setItem(CHANGES_KEY, JSON.stringify(filteredChanges));
};

/**
 * Obtener cambios pendientes
 */
export const getPendingChanges = () => {
  return JSON.parse(localStorage.getItem(CHANGES_KEY) || '[]');
};

/**
 * Limpiar todos los overrides y cambios
 */
export const clearAllPasswordOverrides = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(ADMIN_STORAGE_KEY);
  localStorage.removeItem(CHANGES_KEY);
};

/**
 * Generar script para aplicar cambios al database.json y .env
 */
export const generateUpdateScript = () => {
  const changes = getPendingChanges();
  if (changes.length === 0) return null;

  let script = `# Script para aplicar cambios de contraseñas
# Ejecutar desde la raíz del proyecto

`;

  // Cambios de clientes (database.json)
  const clientChanges = changes.filter(c => c.type === 'client');
  if (clientChanges.length > 0) {
    script += `# 1. Actualizar database.json\n`;
    clientChanges.forEach(change => {
      const client = database.clientes.find(c => c.id === change.clientId);
      script += `# ${change.clientId} - ${client?.nombre}\n`;
      script += `sed -i 's/"passwordHash": ".*"/"passwordHash": "${change.newPasswordHash}"/g' src/database.json\n`;
    });
    script += `\n`;
  }

  // Cambios de admin (.env)
  const adminChanges = changes.filter(c => c.type === 'admin');
  if (adminChanges.length > 0) {
    script += `# 2. Actualizar .env local\n`;
    adminChanges.forEach(change => {
      script += `# Admin password\n`;
      script += `sed -i 's/REACT_APP_ADMIN_PASSWORD_HASH=.*/REACT_APP_ADMIN_PASSWORD_HASH=${change.newPasswordHash}/g' .env\n`;
    });
    script += `\n# 3. Actualizar variables en Vercel\n`;
    script += `# Ve a Vercel Dashboard → Settings → Environment Variables\n`;
    script += `# Actualiza: REACT_APP_ADMIN_PASSWORD_HASH=${adminChanges[adminChanges.length - 1].newPasswordHash}\n`;
    script += `\n`;
  }

  script += `# 4. Commit y deploy\n`;
  script += `git add .\n`;
  script += `git commit -m "Update passwords from admin panel"\n`;
  script += `git push\n`;

  return script;
};

/**
 * Generar nuevo database.json con cambios aplicados
 */
export const generateUpdatedDatabaseJson = () => {
  const changes = getPendingChanges();
  if (changes.length === 0) return null;

  // Crear copia profunda del database original
  const updatedDatabase = JSON.parse(JSON.stringify(database));
  
  // Aplicar cambios de clientes
  const clientChanges = changes.filter(c => c.type === 'client');
  clientChanges.forEach(change => {
    const clientIndex = updatedDatabase.clientes.findIndex(c => c.id === change.clientId);
    if (clientIndex !== -1) {
      updatedDatabase.clientes[clientIndex].passwordHash = change.newPasswordHash;
    }
  });

  return JSON.stringify(updatedDatabase, null, 2);
};

/**
 * Generar instrucciones step-by-step para aplicar cambios manualmente
 */
export const generateManualInstructions = () => {
  const changes = getPendingChanges();
  if (changes.length === 0) return null;

  let instructions = `## 📋 INSTRUCCIONES PARA APLICAR CAMBIOS A PRODUCCIÓN

### PASO 1: Actualizar database.json
`;

  // Cambios de clientes
  const clientChanges = changes.filter(c => c.type === 'client');
  if (clientChanges.length > 0) {
    instructions += `
1. Abre: \`src/database.json\`
2. Busca y reemplaza:

`;
    
    clientChanges.forEach((change, index) => {
      const client = database.clientes.find(c => c.id === change.clientId);
      const originalHash = client?.passwordHash;
      instructions += `**${change.clientId} (${client?.nombre}):**
   - Buscar: \`"passwordHash": "${originalHash}"\`
   - Reemplazar: \`"passwordHash": "${change.newPasswordHash}"\`

`;
    });
  }

  // Cambios de admin
  const adminChanges = changes.filter(c => c.type === 'admin');
  if (adminChanges.length > 0) {
    instructions += `
### PASO 2: Actualizar .env (Admin)

1. Abre: \`.env\`
2. Buscar: \`REACT_APP_ADMIN_PASSWORD_HASH=...\`
3. Reemplazar: \`REACT_APP_ADMIN_PASSWORD_HASH=${adminChanges[adminChanges.length - 1].newPasswordHash}\`

### PASO 3: Actualizar Vercel
1. Ve a Vercel Dashboard → Tu proyecto → Settings → Environment Variables
2. Edita: \`REACT_APP_ADMIN_PASSWORD_HASH\`
3. Nuevo valor: \`${adminChanges[adminChanges.length - 1].newPasswordHash}\`
4. Guarda y Redeploy

`;
  }

  instructions += `
### PASO 4: Commit y Push
\`\`\`bash
git add .
git commit -m "Apply password changes from admin panel"
git push
\`\`\`

### PASO 5: Verificar
- Las nuevas contraseñas funcionarán para todos los usuarios
- Puedes limpiar los cambios pendientes desde el panel

---
*Generado automáticamente por el sistema de gestión de contraseñas*
`;

  return instructions;
};

/**
 * Obtener resumen de cambios pendientes
 */
export const getPendingChangesSummary = () => {
  const changes = getPendingChanges();
  const summary = {
    total: changes.length,
    clients: changes.filter(c => c.type === 'client').length,
    admin: changes.filter(c => c.type === 'admin').length > 0
  };
  
  return summary;
};